/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/01/22
 */

import { useAuth } from '@/components/auth/useAuth';
import config from '@/config';

import { User } from '@/wpt-lib/interfaces/User';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { useGetRequest } from '@/utils/useStatefulRequests';
import React, { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';

export interface IAssignmentContext {
    currentAssignment?: Assignment;
    loading: boolean;
    error: boolean;
    assignments?: Assignment[];
    activeAssignments: Assignment[];
    inactiveAssignments: Assignment[];
    user?: User;
    setAssignment(assignment: Assignment): void;
}

export const AssignmentContext = createUnprovisionedContext<IAssignmentContext>('AssignmentContext');

export const AssignmentProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { authState } = useAuth();
    const { loading, error, makeRequest, responseData: meUser } = useGetRequest<User>();

    const [currentAssignment, _setCurrentAssignment] = useState<Assignment | undefined>();
    const setCurrentAssignment = (assignment?: Assignment) => {
        _setCurrentAssignment(assignment);
        if (assignment) {
            localStorage.setItem(config.auth.assignmentStorageKey, assignment.assignmentNumber);
        } else {
            localStorage.removeItem(config.auth.assignmentStorageKey);
        }
    };

    const allAssignments = useMemo(
        () =>
            (meUser?.assignments ?? []).sort((a, b) => {
                /* Sort by active/inactive status */
                if (a.inactive !== b.inactive) {
                    return a.inactive ? 1 : -1;
                }

                /* Sort by FTE with non-zero FTE coming before zero FTE */
                if (a.fte > 0 !== b.fte > 0) {
                    return b.fte - a.fte;
                }

                /* Sort by assignment number */
                return b.assignmentNumber.localeCompare(a.assignmentNumber);
            }),
        [meUser],
    );

    const allActiveAssignments = useMemo(() => allAssignments.filter((assign) => !assign.inactive), [allAssignments]);

    const allInActiveAssignments = useMemo(() => {
        return allAssignments.filter((assign) => assign.inactive);
    }, [allAssignments]);

    useLayoutEffect(() => {
        if (authState.isAuthenticated) {
            if (!currentAssignment && allAssignments.length) {
                let assignmentToSet = allAssignments[0];

                const storedAssignmentNumber = localStorage.getItem(config.auth.assignmentStorageKey);
                if (storedAssignmentNumber) {
                    const found = allAssignments.find((a) => a.assignmentNumber === storedAssignmentNumber);
                    if (found) {
                        assignmentToSet = found;
                    }
                }
                setCurrentAssignment(assignmentToSet);
            }
        } else {
            setCurrentAssignment(undefined);
        }
    }, [authState, allAssignments, currentAssignment]);

    useAsyncLayoutEffect(async () => {
        if (authState.isAuthenticated) {
            try {
                await makeRequest('users/me');
            } catch (e) {
                // handled by hook
            }
        }
    }, [authState]);

    const setAssignment = useCallback((assignment: Assignment) => {
        setCurrentAssignment(assignment);
    }, []);

    const context: IAssignmentContext = {
        loading,
        error: !!error,
        currentAssignment,
        user: meUser,
        assignments: allAssignments,
        activeAssignments: allActiveAssignments,
        inactiveAssignments: allInActiveAssignments,
        setAssignment,
    };

    return <AssignmentContext.Provider value={context}>{children}</AssignmentContext.Provider>;
};

export default AssignmentProvider;
